import React, {
  useCallback,
  useEffect,
  StrictMode,
} from 'react';
import ReactDOM from 'react-dom/client';
import {
  RouterProvider,
  Link,
  createRouter,
  createHashHistory,
} from '@tanstack/react-router';
import { Auth0Provider } from '@auth0/auth0-react';
import { MockAuth0Provider } from './mock-auth';
import { useAppAuth } from './hooks/use-app-auth';
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  CombinedGraphQLErrors,
  ServerError,
} from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { ErrorLink } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { debounce } from 'rambdax';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import './i18n';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { clearSessionStorage } from './utils/storage-helpers';
import {
  initializeApiBaseUrl, getApiBaseUrl, stripBuildParamFromUrl,
} from './utils/api-base-url';
import { TourProvider } from './contexts/tour-context';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

const CREDIT_PULSE_TLD_REGEX = /\.creditpulse\.com\//;

const parseSearch = (search: string) => {
  // Extract the part after the last slash
  const lastSegment = search.split('/').pop() || '';
  const searchParams = new URLSearchParams(lastSegment);
  return Object.fromEntries(searchParams);
};

const stringifySearch = (searchObj: Record<string, any>) => {
  return new URLSearchParams(searchObj).toString();
};

// Not Found component
function NotFoundComponent () {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: 3,
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: 600,
        }}
      >
        <Box
          alt='Credit Pulse Logo'
          component='img'
          src='https://media.creditpulse.com/branding/credit-pulse-banner-logo-transparent.png'
          sx={{
            height: 60,
            marginBottom: 4,
          }}
        />
        <Typography
          sx={{
            fontSize: '120px',
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            marginBottom: 2,
          }}
          variant='h1'
        >
          404
        </Typography>
        <Typography
          sx={{
            marginBottom: 2,
            color: '#333',
          }}
          variant='h4'
        >
          Page Not Found
        </Typography>
        <Typography
          sx={{
            marginBottom: 4,
            color: '#666',
          }}
          variant='body1'
        >
          The page you&apos;re looking for doesn&apos;t exist or has been moved.
        </Typography>
        <Link
          style={{ textDecoration: 'none' }}
          to='/'
        >
          <Button
            size='large'
            sx={{
              paddingX: 4,
              paddingY: 1.5,
            }}
            variant='contained'
          >
            Go to Home
          </Button>
        </Link>
      </Box>
    </Box>
  );
}

// Error component for when JavaScript errors occur
function ErrorComponent () {
  const handleGoHome = async () => {
    await clearSessionStorage();
    window.location.href = '/#/';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        padding: 3,
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: 600,
        }}
      >
        <Typography
          sx={{
            fontSize: '100px',
            marginBottom: 1,
          }}
          variant='h1'
        >
          🤦
        </Typography>
        <Typography
          sx={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: theme.palette.error.main,
            marginBottom: 1,
          }}
          variant='h2'
        >
          Well, this is embarrassing...
        </Typography>
        <Typography
          sx={{
            marginBottom: 2,
            color: '#666',
            fontStyle: 'italic',
          }}
          variant='h6'
        >
          This page didn&apos;t pass our credit check
        </Typography>
        <Typography
          sx={{
            marginBottom: 4,
            color: '#666',
          }}
          variant='body1'
        >
          We encountered an unexpected error. We have been notified of this error and will investigate it shortly.
        </Typography>
        <Button
          size='large'
          sx={{
            paddingX: 4,
            paddingY: 1.5,
          }}
          variant='contained'
          onClick={handleGoHome}
        >
          Go Home
        </Button>
      </Box>
    </Box>
  );
}

// Resolve the API base URL (reading any ?build= override) and then strip the param from the
// URL before the router is created — createHashHistory() captures the hash into router state,
// so a ?build= after the '#' must be removed first or it reappears on the next navigation.
initializeApiBaseUrl(process.env.REACT_APP_API_BASE_URL ?? '');
stripBuildParamFromUrl();

// Create a new router instance
const router = createRouter({
  routeTree,
  parseSearch,
  stringifySearch,
  history: createHashHistory(),
  defaultNotFoundComponent: NotFoundComponent,
  defaultErrorComponent: ErrorComponent,
  context: {
    auth: undefined!, // This will be set after we wrap the app in an AuthProvider
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  // eslint-disable-next-line no-unused-vars
  interface Register {
    router: typeof router
  }
}

const MAX_API_RETRY_DELAY = 15000; // 15 seconds
const MAX_API_RETRY_ATTEMPTS = 5;
const API_RETRY_DELAY_INITIAL = 250; // 250ms
const UNAUTHORIZED_ERROR_MESSAGE = 'Received status code 401';
const MISSING_REFRESH_TOKEN_ERROR_MESSAGE = 'Missing Refresh Token';
const INVALID_TOKEN_ERROR_MESSAGE = 'Invalid Token';
const LOGIN_REQUIRED_ERROR_MESSAGE = 'Login Required';
const USER_ACTIVITY_DEBOUNCE_MS = 5000; // 5 seconds
const USER_ACTIVITY_EVENT_TYPES = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
];

const isAuthenticationErrorMessage = (message: string) => {
  return message.includes(UNAUTHORIZED_ERROR_MESSAGE) ||
    message.includes(MISSING_REFRESH_TOKEN_ERROR_MESSAGE) ||
    message.includes(INVALID_TOKEN_ERROR_MESSAGE) ||
    message.includes(LOGIN_REQUIRED_ERROR_MESSAGE);
};

const isQueryCancellationError = (error: { name?: string; message?: string }) => {
  return error.name === 'AbortError' || error.name === 'Invariant Violation';
};

// Check if we should skip authentication for testing
// Only allow auth bypass in development environment for security
const SKIP_AUTH = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DISABLE_AUTH === 'true';
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 Auth Debug:', {
    REACT_APP_DISABLE_AUTH: process.env.REACT_APP_DISABLE_AUTH,
    SKIP_AUTH,
    NODE_ENV: process.env.NODE_ENV,
  });
}

function App () {
  const auth = useAppAuth();

  // Strip query parameters from URL on initial page load to prevent them from being
  // interpreted as part of the route path. Preserve Auth0-related params as they're
  // needed for the authentication flow.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    // The ?build= API override param is stripped earlier at module scope (see
    // stripBuildParamFromUrl in utils/api-base-url.ts), so it never reaches this list.
    const allowedParams = [
      'invitation',
      'organization',
      'code',
      'state',
      'error',
      'error_description',
    ];
    const hasNonAuthParams = Array.from(searchParams.keys()).some(
      (key) => !allowedParams.includes(key),
    );

    if (hasNonAuthParams) {
      // Build new URL with only Auth0 params preserved
      let newUrl = window.location.origin + window.location.pathname;

      const authParams = new URLSearchParams();
      allowedParams.forEach((param) => {
        const value = searchParams.get(param);
        if (value) {
          authParams.set(param, value);
        }
      });

      if (authParams.toString()) {
        newUrl += '?' + authParams.toString();
      }

      // Preserve hash for hash routing
      newUrl += window.location.hash || '#/';

      // Update URL without causing a page reload
      window.history.replaceState({}, '', newUrl);
    }
  }, []); // Run only once on mount

  // Log out the user if the session has expired and log the error message
  const handleLogout = useCallback(async (errorMessage: string) => {
    console.warn('Session expired, redirecting to login.', { error: errorMessage });
    // NOTE: this clears all sessionStorage, including any active ?build= PR override
    // (see utils/api-base-url.ts). After re-login a developer must re-add ?build= to the URL.
    await clearSessionStorage();
    auth.logout({ logoutParams: { returnTo: window.location.origin + window.location.pathname + window.location.hash } });
    return '';
  }, [ auth ]);

  // When a user logs in, an access token is fetched from Auth0. The token expiration is specific to the API
  // and defined in the Auth0 dashboard (under Applications -> APIs -> {API NAME} -> Settings).
  // When the token expires, a new one is automatically fetched if their session is still active.
  // The session expiration settings are tenant-specific and defined in the Auth0 dashboard (under Settings -> Advanced).
  const getToken = useCallback(async () => {
    try {
      // If the current token in local storage is not expired, a new one will not be fetched automatically
      // If the session is no longer valid, the user will be logged out as soon as the token is checked
      const token = await auth.getAccessTokenSilently();
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Token obtained:', token ? 'Success' : 'Failed');
      }
      return token;
    } catch (tokenError: unknown) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Token error:', tokenError);
      }
      return handleLogout(tokenError instanceof Error ? tokenError.message : 'Unknown error');
    }
  }, [ auth, handleLogout ]);

  // This is tied to user activity (mouse movement, key presses, etc.), and is used to check the token
  // to see if it is still valid while the user is interacting with the app.
  // This is debounced to prevent excessive calls to check the token.
  const debouncedGetToken = debounce((_event: unknown) => getToken(), USER_ACTIVITY_DEBOUNCE_MS, true);

  // This effect sets up event listeners for user activity within the app.
  // If a session is no longer active, the user will be returned to the
  // login page as soon as one of these events are detected. This prevents a user from
  // viewing the app while a session is no longer valid.
  useEffect(() => {
    const handleUserActivity = () => {
      // Pass null since debounce expects an event parameter but we don't need it
      debouncedGetToken(null);
    };

    // only start listening to user activity if the user is authenticated
    if (!auth.isLoading && auth.isAuthenticated) {
    // Add event listeners for user activity
      USER_ACTIVITY_EVENT_TYPES.forEach((eventType) => {
        window.addEventListener(eventType, handleUserActivity);
      });
    }

    return () => {
      // Remove event listeners for user activity when the component unmounts
      USER_ACTIVITY_EVENT_TYPES.forEach((eventType) => {
        window.removeEventListener(eventType, handleUserActivity);
      });
    };
  }, [ debouncedGetToken, auth ]);

  // This link is used to send the GraphQL operation to a specific URL.
  const httpLink = new HttpLink({
    uri: getApiBaseUrl(),
  });

  // This link is used to retry the GraphQL operation if it fails.
  const retryLink = new RetryLink({
    delay: {
      initial: API_RETRY_DELAY_INITIAL,
      max: MAX_API_RETRY_DELAY,
      jitter: true,
    },
    attempts: {
      max: MAX_API_RETRY_ATTEMPTS,
      // Only retry if the error is not due to error around authentication
      retryIf: (error, _operation) => !isAuthenticationErrorMessage(error.message) && !isQueryCancellationError(error),
    },
  });

  // This link is used to remove the __typename field from the variables.
  const removeTypenameLink = removeTypenameFromVariables();

  // This link is called after the GraphQL operation completes and execution is moving back up your link chain.
  // The function should not return a value unless you want to retry the operation.
  const errorLink = new ErrorLink(({ error }) => {
    let errorMessages: string[] = [];
    if (CombinedGraphQLErrors.is(error)) {
      errorMessages = (error.errors || []).map((err) => err.message);
    } else if (ServerError.is(error)) {
      errorMessages = [ error.message ];
    }
    const authErrorMessage = errorMessages.find(isAuthenticationErrorMessage);
    if (authErrorMessage) {
      handleLogout(authErrorMessage);
    }
  });

  // This function is called before the GraphQL operation is sent to the server.
  // It is used to add the user's access token to the request headers.
  const authLink = setContext(async (_, { headers }) => {
    const token = await getToken();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: ApolloLink.from([
      errorLink,
      authLink,
      retryLink,
      removeTypenameLink,
      httpLink,
    ]),
    defaultOptions: {
      mutate: {
        errorPolicy: 'all',
      },
    },
  });

  const {
    isLoading: auth0Loading,
    isAuthenticated,
    loginWithRedirect,
  } = auth;

  const searchParams = window.location.search;
  const auth0Invite = searchParams.match(/[?&]invitation=([^&]+)/)?.[1];
  const auth0Org = searchParams.match(/[?&]organization=([^&]+)/)?.[1];
  const auth0Code = searchParams.match(/[?&]code=([^&]+)/)?.[1];
  const auth0State = searchParams.match(/[?&]state=([^&]+)/)?.[1];
  // By including auth0Code and auth0State in the loading state, we can prevent
  // a flashing of the loading page, and API calls while the auth0 redirect is happening
  const isLoadingAuth0 = auth0Loading || !!auth0Code || !!auth0State;

  React.useEffect(() => {
    if (auth0Loading || isAuthenticated) {
      return;
    }
    const fn = async () => {
      await loginWithRedirect({
        appState: { returnTo: window.location.origin + window.location.pathname + window.location.hash },
        authorizationParams: (auth0Invite && auth0Org)
          ? {
              organization: auth0Org,
              invitation: auth0Invite,
            }
          : undefined,
      });
    };
    fn();
  }, [
    auth0Invite,
    auth0Loading,
    auth0Org,
    isAuthenticated,
    loginWithRedirect,
  ]);

  if (!isAuthenticated || isLoadingAuth0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <ApolloProvider client={client}>
      <RouterProvider
        context={{ auth }}
        router={router}
      />
    </ApolloProvider>
  );
}

// Auth Provider Component
const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  if (SKIP_AUTH) {
    return <MockAuth0Provider>{children}</MockAuth0Provider>;
  }

  return (
    <Auth0Provider
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      }}
      cacheLocation='localstorage'
      clientId={process.env.REACT_APP_CLIENT_ID || ''}
      domain={process.env.REACT_APP_AUTH0_DOMAIN || ''}
      onRedirectCallback={(appState) => {
        const returnTo = appState?.returnTo;
        if (returnTo) {
          // If returnTo is not a valid domain, do nothing
          if (!returnTo.startsWith('http://localhost:') && !CREDIT_PULSE_TLD_REGEX.test(returnTo)) {
            console.warn('Invalid return URL detected:', returnTo);
            return;
          }
          if (process.env.NODE_ENV === 'development') {
            console.log('Redirecting to:', returnTo);
          }
          window.location.href = returnTo;
        }
      }}
    >
      {children}
    </Auth0Provider>
  );
};

// Silently handle AbortError rejections that escape Apollo's link chain.
// These occur when in-flight queries are cancelled (e.g., component unmount,
// search debouncing) and the internal rejection has no subscriber to catch it.
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason instanceof DOMException && event.reason.name === 'AbortError') {
    event.preventDefault();
  }
});

// Render the app
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <TourProvider>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <App />
          </ThemeProvider>
        </AuthProvider>
      </TourProvider>
    </StrictMode>,
  );
}
